import React, { createContext, useContext, useMemo } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import sources from '../generated/wordpress/sources.json';
import {
    localeFromPath,
    normalizePath,
    localizeData,
    selectLocalizedData,
    getTranslations,
} from './routing';

const LocaleContext = createContext({
    locale: 'en',
    sources,
    routes: new Set(),
    siteUrl: 'https://cloudoora.com',
    translations: [],
});
export const useLocale = () => useContext(LocaleContext);

function LocalizedPage({ element, pageProps }) {
    const { allSitePage, site } = useStaticQuery(graphql`
        query WordpressLocaleRoutes {
            allSitePage {
                nodes {
                    path
                }
            }
            site {
                siteMetadata {
                    siteUrl
                }
            }
        }
    `);
    const pathname = pageProps.location?.pathname || pageProps.path || '/';
    const locale = pageProps.pageContext?.locale || localeFromPath(pathname);
    const settings = useMemo(() => {
        const routes = new Set(
            allSitePage.nodes.map((page) => normalizePath(page.path))
        );
        return {
            locale,
            routes,
            sources,
            siteUrl: site.siteMetadata.siteUrl,
            translations: getTranslations(pathname, routes, sources),
        };
    }, [locale, pathname, allSitePage, site]);
    const data = useMemo(
        () => localizeData(pageProps.data, settings),
        [pageProps.data, settings]
    );
    return (
        <LocaleContext.Provider value={settings}>
            {React.cloneElement(element, { data })}
        </LocaleContext.Provider>
    );
}

export function useLocalizedWordpressData(data) {
    const settings = useLocale();
    return useMemo(
        () =>
            localizeData(selectLocalizedData(data, settings.locale), settings),
        [data, settings]
    );
}

export const wrapPageElement = ({ element, props }) => (
    <LocalizedPage element={element} pageProps={props} />
);
